import React, { useState } from 'react';
import { Box, Tabs, Tab, styled, Fade } from '@mui/material';
import { useDispatch } from 'react-redux';
import {
  getQuestionState,
  resetAIMarking,
} from '../../../../../store/QuestionSlice';
import McqQuestions from './McqQuestions';
import TheoryQuestions from './TheoryQuestions';
import SubmissionPopup from './SubmissionPopup';
import {
  getBannerStatusesFromLocalStorage,
  setBannerStatusToLocalStorage,
} from '../../../../../helper';
import { useAppSelector } from '../../../../../hooks';
import { ISubTopic } from '../../../../../store/SubTopicSlice';

interface SectionTabsProps {
  subTopic: ISubTopic;
}

const CustomTabs = styled(Tabs)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  borderRadius: '15px',
  padding: '5px',
  display: 'inline-flex',
  '& .MuiTabs-indicator': {
    display: 'none',
  },
}));

const CustomTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 'bold',
  fontSize: '14px',
  borderRadius: '11px',
  padding: '0px 15px',
  color: '#000',
  '&.Mui-selected': {
    backgroundColor: '#1C2536',
    color: '#FFF',
  },
  [theme.breakpoints.up('sm')]: {
    padding: '0px 40px',
  },
  [theme.breakpoints.up('md')]: {
    padding: '0px 40px',
  },
  [theme.breakpoints.up('lg')]: {
    padding: '0px 40px',
  },
}));

const PracticeQuestion: React.FC<SectionTabsProps> = ({ subTopic }) => {
  const dispatch = useDispatch();
  const bannerStatuses = getBannerStatusesFromLocalStorage();
  const questionState = useAppSelector(getQuestionState);

  const [activeTab, setActiveTab] = useState(0);
  const [isBannerVisible, setShowBanner] = useState(() => {
    return !bannerStatuses.SubmissionPopupShown;
  });

  const questionList = questionState.list;

  const mcqQuestionList = questionList.filter(
    (question) => !question.isOpenEnded
  );
  const theoryQuestionList = questionList.filter(
    (question) => question.isOpenEnded
  );

  const handleBannerClose = () => {
    setShowBanner(false);
    setBannerStatusToLocalStorage('SubmissionPopupShown', true);
  };

  return (
    <>
      {questionList.length && subTopic.resources?.length ? (
        <Box id="practice-questions">
          <Box display="flex" justifyContent="center">
            <CustomTabs
              value={activeTab}
              onChange={(e, newValue) => {
                dispatch(resetAIMarking());
                setActiveTab(newValue);
              }}
            >
              <CustomTab label="MCQ Questions" />
              {theoryQuestionList.length > 0 ? (
                <CustomTab
                  label={
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      Smart Answers
                      <img
                        src={`${process.env.PUBLIC_URL}/images/backgrounds/star.png`}
                        alt="ai-logo"
                        style={{
                          width: '20px',
                          height: '18px',
                        }}
                      />
                    </span>
                  }
                />
              ) : null}
            </CustomTabs>
          </Box>
          <Box mt={2}>
            <Fade in={activeTab === 0} timeout={300} unmountOnExit>
              <div>
                <McqQuestions questionList={mcqQuestionList} />
              </div>
            </Fade>
            <Fade in={activeTab === 1} timeout={300} unmountOnExit>
              <Box sx={{ position: 'relative' }}>
                {isBannerVisible && (
                  <SubmissionPopup
                    open={isBannerVisible}
                    onClose={handleBannerClose}
                  />
                )}
                <TheoryQuestions questionList={theoryQuestionList} />
              </Box>
            </Fade>
          </Box>
        </Box>
      ) : null}
    </>
  );
};

export default PracticeQuestion;
